import { useQuery } from '@apollo/client/react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import { tss } from 'tss-react/mui';

import { Skeleton } from '@mui/material';

import { graphql } from '@/gql';

import { extractPaginationProps } from '@/utils/queries/queryExtracts';

import { POST_LIMIT } from '@/modules/group/noticeboard/PostCollection';
import { MultiGroupPostCollection } from '@/app/(main)/myHub/home/_components/MultiGroupPostCollection';

import { ServiceMultiGroupPinnedPostsAccordion } from './ServiceMultiGroupPinnedPostsAccordion';

interface Props {
  groupIds: string[] | null;
  machineName: string;
}

export const SERVICE_GROUPS_POSTS_QUERY = graphql(`
  query ServiceGroupsPostsQuery(
    $machineName: String!
    $first: Int
    $after: String
    $groupIds: [ID]
  ) {
    service(machineName: $machineName) {
      _id
      groupPostsConnection(
        first: $first
        after: $after
        filterBy: { groupIds: $groupIds }
        orderBy: CREATED_DESC
      ) @connection(key: "dashboardposts") {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...multiGroupsPostFragment
          }
        }
      }
    }
  }
`);

const useStyles = tss.create(() => {
  return {
    listSubheaderBar: { display: 'flex', alignItems: 'center' },
  };
});

interface PostCollectionHeaderProps {
  loading: boolean;
  title: ReactNode;
  collectionLength: number;
}

export const PostCollectionHeader = ({
  loading,
  title,
  collectionLength,
}: PostCollectionHeaderProps) => {
  const { classes } = useStyles();

  return collectionLength || loading ? (
    <div className={classes.listSubheaderBar}>
      <ListSubheader disableSticky>{title}</ListSubheader>
      {loading ? <Skeleton /> : null}
    </div>
  ) : null;
};

export const ServiceMultiGroupPostsEntry = ({
  machineName,
  groupIds,
}: Props) => {
  const [fetchOutstanding, setFetchOutstanding] = useState(false);

  const { data, loading, fetchMore } = useQuery(SERVICE_GROUPS_POSTS_QUERY, {
    variables: {
      machineName: machineName || '',
      first: POST_LIMIT,
      after: '',
      groupIds,
    },
    skip: !machineName,
    fetchPolicy: 'cache-and-network',
    context: { maxAttempts: 0 },
  });

  const service = data?.service;

  const {
    nodes: posts,
    endCursor,
    hasNextPage: hasMorePosts,
  } = extractPaginationProps(service?.groupPostsConnection);

  return (
    <>
      <ServiceMultiGroupPinnedPostsAccordion
        machineName={machineName}
        groupIds={groupIds}
      />
      <PostCollectionHeader
        loading={loading}
        title={'Recent posts'}
        collectionLength={posts.length}
      />
      <MultiGroupPostCollection
        serviceMachineName={machineName}
        hasMorePosts={hasMorePosts}
        loadMorePosts={async () => {
          if (fetchOutstanding) {
            return;
          }

          setFetchOutstanding(true);
          await fetchMore({
            variables: {
              machineName: machineName || '',
              first: POST_LIMIT,
              after: endCursor,
              groupIds,
            },
          });
          setFetchOutstanding(false);
        }}
        posts={posts}
        loading={loading}
      />
    </>
  );
};
